import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { RootState } from "../../app/store";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import apiClient from "../../core/config/api";
import { downloadFileName, uploadFileNameError } from "../../utils/fileNames";
import ResolveCatchAllModal, { ResolveCatchAllSelection } from "../../components/ResolveCatchAllModal";
import CatchAllResolverPromo from "../../components/CatchAllResolverPromo";
import { usePhoneGate } from "../../components/phone/usePhoneGate";
import "./email_verify.css";

interface SingleVerifierHistoryItem {
    id: number;
    label: string;
    email?: string | null;
    file_name?: string | null;
    created_at: string;
    status: string;
    status_label?: string | null;
    can_download_results: boolean;
    can_download_request_data: boolean;
    source: "single" | "bulk";
    progress_percent?: number;
    processed_count?: number;
    total_count?: number;
    valid_count?: number;
    invalid_count?: number;
    catch_all_count?: number;
    risky_all_count?: number;
    unknown_count?: number;
    invalid_domain_count?: number;
    do_not_send_count?: number;
    do_not_mail_count?: number;
    catch_all_resolvable?: boolean;
    catch_all_resolve?: {
        submitted: number;
        resolved: number;
        status: "in_progress" | "completed" | "failed";
    } | null;
}

type TooltipVariant = "valid" | "invalid" | "catchall" | "risky" | "unknown" | "domain";

// Emails per uploaded file (1 lakh). Must match MAX_BULK_EMAILS in
// backend/app/utils/bulk_file_parser.py, which also checks Excel/Word/PDF files.
const BULK_MAX_EMAILS = 100000;
const MAX_QUICK_EMAILS = 20;
const SUPPORTED_BULK_EXTENSIONS = [".csv", ".txt", ".xlsx", ".xls", ".docx", ".pdf"];
const PLAIN_TEXT_BULK_EXTENSIONS = [".csv", ".txt"];

const parseEmails = (raw: string): string[] => {
    return raw
        .split(/[,\n]+/)
        .map((entry) => entry.trim())
        .filter((entry) => entry.length > 0)
        .slice(0, MAX_QUICK_EMAILS);
};

const Email_Verify = () => {
    const location = useLocation();
    const navigate = useNavigate();
    // Free plan users verify their phone before an instant verification and
    // before downloading results (server-enforced; this opens the modal).
    const { requirePhone, handlePhoneError, phoneModal } = usePhoneGate();
    const { } = useSelector((state: RootState) => state.auth);

    const initialTab: "single" | "bulk" = new URLSearchParams(location.search).get("tab") === "bulk" ? "bulk" : "single";
    const [activeTab, setActiveTab] = useState<"single" | "bulk">(initialTab);
    const [emailInput, setEmailInput] = useState("");
    const [emailChips, setEmailChips] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [, setShowContainer] = useState(false);
    const [progress, setProgress] = useState(0);
    const [processedCount, setProcessedCount] = useState(0);
    const [batchTotal, setBatchTotal] = useState(0);
    const [verificationResults, setVerificationResults] = useState<any[]>([]);

    const [quickVerifyError, setQuickVerifyError] = useState<string | null>(null);

    const [fileName, setFileName] = useState("");
    const [bulkFile, setBulkFile] = useState<File | null>(null);
    const [bulkLoading, setBulkLoading] = useState(false);
    const [bulkError, setBulkError] = useState<string | null>(null);
    const [bulkUploadError, setBulkUploadError] = useState<string | null>(null);
    const [bulkFileProcessing, setBulkFileProcessing] = useState(false);
    const [showBulkVerificationPopup, setShowBulkVerificationPopup] = useState(false);

    const [historyItems, setHistoryItems] = useState<SingleVerifierHistoryItem[]>([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState<string | null>(null);
    const [historySearch, setHistorySearch] = useState("");
    const [downloadingFiles, setDownloadingFiles] = useState<{ [key: string]: boolean }>({});
    const [deletingFiles, setDeletingFiles] = useState<{ [key: string]: boolean }>({});
    const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
    const [resolvingCatchAllIds, setResolvingCatchAllIds] = useState<{ [key: number]: boolean }>({});
    const [resolveCatchAllTarget, setResolveCatchAllTarget] = useState<{ id: number; count: number } | null>(null);
    // Rows whose Catch-All resolution was just queued from this page. Shows
    // the "Resolving" state immediately, before the backend's
    // catch_all_resolve reports in_progress - cleared once it does.
    const [resolveQueuedIds, setResolveQueuedIds] = useState<Set<number>>(new Set());
    // Bulk rows that were seen mid-run and have just reached "completed" -
    // they show the green "Completed 100%" pill for a few seconds before
    // switching to the per-outcome count icons (see renderBulkStatus).
    const [justCompletedIds, setJustCompletedIds] = useState<Set<number>>(new Set());
    const prevStatusesRef = useRef<Map<number, string>>(new Map());
    const completedTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [pageSize] = useState(5);
    const [goToPageInput, setGoToPageInput] = useState("");
    const historyPollRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const historySectionRef = useRef<HTMLDivElement | null>(null);
    const chipInputRef = useRef<HTMLInputElement | null>(null);
    const historyRequestSeqRef = useRef(0);
    const verifyRunSeqRef = useRef(0);
    const isSubmittingVerifyRef = useRef(false);
        // Guards the displayed percentage against ever visibly regressing within
    // one run (e.g. a stray state reset elsewhere briefly emptying
    // verificationResults) - tracks the highest processed count actually
    // observed so far for the current runSeq.
    const peakProcessedCountRef = useRef(0);

    const [countTooltip, setCountTooltip] = useState<{
        top: number;
        left: number;
        text: string;
        variant: TooltipVariant;
    } | null>(null);

    const showCountTooltip = (
        e: React.SyntheticEvent<HTMLDivElement>,
        text: string,
        variant: TooltipVariant
    ) => {
        const rect = e.currentTarget.getBoundingClientRect();
        setCountTooltip({
            top: rect.bottom,
            left: rect.left + rect.width / 2,
            text,
            variant,
        });
    };

    const hideCountTooltip = () => setCountTooltip(null);

    const fetchHistory = async (page: number = currentPage, tab: "single" | "bulk" = activeTab, silent: boolean = false) => {
        const requestSeq = ++historyRequestSeqRef.current;
        if (!silent) setHistoryLoading(true);
        // Only clear a previous error for a user-initiated fetch. A silent
        // background poll (historyPollRef) shouldn't wipe out an error that
        // was just set by a direct user action (e.g. a failed submission),
        // often within seconds of it being shown.
        if (!silent) setHistoryError(null);
        try {
            const params: any = {
                page,
                page_size: pageSize,
            };
            const trimmedSearch = historySearch.trim();
            if (trimmedSearch) {
                params.search = trimmedSearch;
            }

            const endpoint = tab === "bulk" ? "/verifier-requests/re/bulk" : "/verifier-requests/re/single";
            const response = await apiClient.get(endpoint, { params });

            const items = response?.data?.items || [];
            const mapped = items.map((item: any) => ({
                id: item.id,
                label: item.label,
                created_at: item.created_at,
                status: item.status,
                status_label: item.status_label,
                can_download_results: item.can_download_results,
                can_download_request_data: item.can_download_request_data,
                file_name: item.file_name,
                email: item.email,
                source: tab,
                progress_percent: item.progress_percent ?? item.progress ?? item.percentage,
                processed_count: item.processed_count ?? item.processed ?? item.done,
                total_count: item.total_count ?? item.total ?? item.total_emails ?? item.data_count,
                valid_count: item.valid_count,
                invalid_count: item.invalid_count,
                catch_all_count: item.catch_all_count,
                risky_all_count: item.risky_all_count,
                unknown_count: item.unknown_count,
                invalid_domain_count: item.invalid_domain_count,
                do_not_send_count: item.do_not_mail_count ?? item.do_not_send_count,
                catch_all_resolve: item.catch_all_resolve ?? item.details?.catch_all_resolve ?? null,
                catch_all_resolvable: !!item.catch_all_resolvable,
            } as SingleVerifierHistoryItem));

            const total = response?.data?.total ?? 0;
            if (requestSeq !== historyRequestSeqRef.current) return;

            setHistoryItems(mapped);
            setTotalPages(Math.ceil(total / pageSize));
            setCurrentPage(page);
            return mapped;
        } catch (error: any) {
            if (requestSeq !== historyRequestSeqRef.current) return [];
            // Only ever show a message the backend deliberately crafted for the
            // user - never fall back to error.message, which for a genuine
            // network failure is axios's raw internal string "Network Error"
            // and isn't something a user should see.
            setHistoryError(
                error?.response?.data?.message ||
                "Unable to load verification history. Please check your connection and try again."
            );
            return [];
        } finally {
            if (requestSeq === historyRequestSeqRef.current && !silent) setHistoryLoading(false);
        }
    };

    useEffect(() => {
        setCurrentPage(1);
        fetchHistory(1, activeTab);
    }, [activeTab]);

    const isFirstSearchRender = useRef(true);
    useEffect(() => {
        if (isFirstSearchRender.current) {
            isFirstSearchRender.current = false;
            return;
        }
        const timeout = setTimeout(() => {
            setCurrentPage(1);
            fetchHistory(1, activeTab);
        }, 400);
        return () => clearTimeout(timeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [historySearch]);

    useEffect(() => {
        if (historyPollRef.current) {
            clearInterval(historyPollRef.current);
            historyPollRef.current = null;
        }

        const pollStatuses = new Set([
            "pending",
            "processing",
            "paused",
            "retrying",
            "restarted",
            "in_progress",
            "inprogress",
        ]);

        const hasPending = historyItems.some(
            (item) =>
                pollStatuses.has((item.status || "").toLowerCase()) ||
                item.catch_all_resolve?.status === "in_progress" ||
                resolveQueuedIds.has(item.id)
        );

        if (hasPending) {
            const intervalMs = 5000;
            historyPollRef.current = setInterval(() => {
                fetchHistory(currentPage, activeTab, true);
            }, intervalMs);
        }

        return () => {
            if (historyPollRef.current) {
                clearInterval(historyPollRef.current);
                historyPollRef.current = null;
            }
        };
    }, [activeTab, historyItems, currentPage, historySearch, resolveQueuedIds]);

    useEffect(() => {
        if (activeTab !== "single" || historyItems.length === 0) return;
        setVerificationResults((prev) => {
            if (prev.length === 0) return prev;
            let changed = false;
            const next = prev.map((result) => {
                const resultEmail = (result.email || "").trim().toLowerCase();
                if (!resultEmail) return result;
                const match = historyItems.find(
                    (item) => item.source === "single" && (item.email || "").trim().toLowerCase() === resultEmail
                );
                if (match?.status && (match.status !== result.status || match.status_label !== result.status_label)) {
                    changed = true;
                    return { ...result, status: match.status, status_label: match.status_label };
                }
                return result;
            });
            return changed ? next : prev;
        });
    }, [historyItems, activeTab]);

    useEffect(() => {
        const prev = prevStatusesRef.current;
        const newlyCompleted: number[] = [];
        historyItems.forEach((item) => {
            const status = (item.status || "").toLowerCase();
            const before = prev.get(item.id);
            if (item.source === "bulk" && status === "completed" && before && before !== "completed" && before !== "failed") {
                newlyCompleted.push(item.id);
            }
            prev.set(item.id, status);
        });
        if (newlyCompleted.length === 0) return;

        setJustCompletedIds((current) => new Set([...current, ...newlyCompleted]));
        completedTimersRef.current.push(setTimeout(() => {
            setJustCompletedIds((current) => {
                const next = new Set(current);
                newlyCompleted.forEach((id) => next.delete(id));
                return next;
            });
        }, 4000));
    }, [historyItems]);

    useEffect(() => () => completedTimersRef.current.forEach(clearTimeout), []);

    // Retry on a failed upload = upload the file again: open the same file
    // picker the upload box uses, so the new file goes through handleFile
    // exactly like a dropped or browsed file (no re-verify of the old request).
    const uploadAreaRef = useRef<HTMLDivElement | null>(null);
    const retryBulkVerification = () => {
        setHistoryError(null);
        const openPicker = () => {
            uploadAreaRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
            fileInputRef.current?.click();
        };
        if (activeTab !== "bulk") {
            handleTabClick("bulk");
            // The upload box renders with the bulk tab; open it once it exists.
            window.setTimeout(openPicker, 0);
        } else {
            openPicker();
        }
    };

    const openResolveCatchAll = (item: SingleVerifierHistoryItem) => {
        const counts = getBulkCounts(item);
        setResolveCatchAllTarget({ id: item.id, count: counts.catchAll });
    };

    const closeResolveCatchAll = () => setResolveCatchAllTarget(null);

    const resolveCatchAll = async (id: number, selection?: ResolveCatchAllSelection) => {
        if (resolvingCatchAllIds[id]) return;
        setResolvingCatchAllIds((prev) => ({ ...prev, [id]: true }));
        try {
            // Customer route: paid from the customer's own credits, owner-only.
            await apiClient.patch(`/verifier-requests/re/${id}/resolve-catch-all`, selection ?? {});
            toast.success("Catch-All emails queued for resolution");
            setResolveQueuedIds((prev) => new Set(prev).add(id));
            setResolveCatchAllTarget(null);
            await fetchHistory(currentPage, activeTab, true);
        } catch (error: any) {
            toast.error(extractMessage(error?.response?.data, "Failed to queue Catch-All resolution. Please try again."));
        } finally {
            setResolvingCatchAllIds((prev) => ({ ...prev, [id]: false }));
        }
    };


    // The resolver runs once per request: after a run, its Unknown results
    // (shown as Risky) are final, so it is only offered again if the run failed.
    const canResolveAgain = (item: SingleVerifierHistoryItem) =>
        !item.catch_all_resolve || item.catch_all_resolve.status === "failed";

    const isResolvingCatchAll = (item: SingleVerifierHistoryItem) =>
        item.catch_all_resolve?.status === "in_progress" || resolveQueuedIds.has(item.id);

    useEffect(() => {
        if (resolveQueuedIds.size === 0) return;
        const confirmed = historyItems
            .filter((item) => resolveQueuedIds.has(item.id) && item.catch_all_resolve?.status === "in_progress")
            .map((item) => item.id);
        if (confirmed.length === 0) return;
        setResolveQueuedIds((prev) => {
            const next = new Set(prev);
            confirmed.forEach((id) => next.delete(id));
            return next;
        });
    }, [historyItems, resolveQueuedIds]);

    const canViewResults = (item: SingleVerifierHistoryItem) =>
        (item.status || "").toLowerCase() === "completed" && item.can_download_results && !isResolvingCatchAll(item);

    const handlePageChange = (page: number) => {
        if (page >= 1 && page <= totalPages) {
            fetchHistory(page, activeTab);
        }
    };

    const handleGoToPage = () => {
        const page = parseInt(goToPageInput);
        if (!isNaN(page) && page >= 1 && page <= totalPages) {
            handlePageChange(page);
            setGoToPageInput(String(page));
        }
    };

    const renderPageNumbers = () => {
        const pages = [];
        const maxVisiblePages = 5;

        if (totalPages <= maxVisiblePages) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
        } else {
            if (currentPage <= 3) {
                pages.push(1, 2, 3, "...", totalPages);
            } else if (currentPage >= totalPages - 2) {
                pages.push(1, "...", totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push(1, "...", currentPage, "...", totalPages);
            }
        }

        return pages;
    };

    const extractMessage = (data: any, fallback: string) => {
        if (!data) return fallback;
        if (typeof data === "string") return data;
        if (typeof data.message === "string") return data.message;
        if (typeof data.detail === "string") return data.detail;
        if (Array.isArray(data.detail) && data.detail.length) {
            const first = data.detail[0];
            if (typeof first === "string") return first;
            if (typeof first?.msg === "string") return first.msg;
        }
        if (Array.isArray(data.detail) && data.detail.length && typeof data.detail[0] === "string") return data.detail[0];
        if (typeof data.error === "string") return data.error;
        if (Array.isArray(data.errors) && data.errors.length) {
            const first = data.errors[0];
            if (typeof first === "string") return first;
            if (first?.msg) return first.msg;
        }
        return fallback;
    };

    const handleFile = async (file: File | null) => {
        if (!file) return;
        setBulkFileProcessing(true);
        setBulkUploadError(null);

        // Extra dots / invalid characters break the download later - ask for a rename now.
        const nameError = uploadFileNameError(file.name);
        if (nameError) {
            setBulkError(nameError);
            setFileName("");
            setBulkFile(null);
            setBulkFileProcessing(false);
            return;
        }

        const lowerName = file.name.toLowerCase();
        if (!SUPPORTED_BULK_EXTENSIONS.some((ext) => lowerName.endsWith(ext))) {
            setBulkError(`Invalid file format. Accepted formats: ${SUPPORTED_BULK_EXTENSIONS.join(", ")}`);
            setFileName("");
            setBulkFile(null);
            setBulkFileProcessing(false);
            return;
        }

        const isPlainTextFormat = PLAIN_TEXT_BULK_EXTENSIONS.some((ext) => lowerName.endsWith(ext));

        try {
            if (isPlainTextFormat) {
                const text = await file.text();
                const lines = text.split(/\r?\n/).filter((line) => line.trim());
                let count = lines.length;
                if (count > 0) {
                    const first = lines[0].toLowerCase();
                    if (first.includes("email")) {
                        count -= 1;
                    }
                }

                if (count > BULK_MAX_EMAILS) {
                    setBulkError(
                        `This file has ${count.toLocaleString("en-US")} emails. You can upload up to ` +
                        `${BULK_MAX_EMAILS.toLocaleString("en-US")} at a time - please split it into smaller files.`
                    );
                    setFileName("");
                    setBulkFile(null);
                    setBulkFileProcessing(false);
                    return;
                }
            }

            setBulkError(null);
            setFileName(file.name);
            setBulkFile(file);
        } catch (err) {
            setBulkError("Unable to read file");
            setFileName("");
            setBulkFile(null);
        } finally {
            setBulkFileProcessing(false);
        }
    };

    const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
        await handleFile(e.target.files?.[0] || null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        await handleFile(e.dataTransfer.files?.[0] || null);
    };

    const resetVerificationState = () => {
        setProgress(0);
        setProcessedCount(0);
        setBatchTotal(0);
        setHistoryError(null);
        setQuickVerifyError(null);
        setVerificationResults([]);
    };

    const commitChipsFromText = (text: string, existing: string[]): string[] => {
        const merged = [...existing];
        for (const part of parseEmails(text)) {
            if (merged.length >= MAX_QUICK_EMAILS) break;
            // Case-insensitive to match the backend's dedupe_by_email -
            // submitQuickVerifyBatch pairs returned ids with these by position.
            const key = part.toLowerCase();
            if (!merged.some((email) => email.toLowerCase() === key)) merged.push(part);
        }
        return merged;
    };

    const getCombinedEmails = () => commitChipsFromText(emailInput, emailChips);

    const handleChipInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (loading) return;
        const value = e.target.value;
        if (/[,\n]/.test(value)) {
            setEmailChips((prev) => commitChipsFromText(value, prev));
            setEmailInput("");
        } else {
            setEmailInput(value);
        }
        resetVerificationState();
    };

    const handleChipInputPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
        if (loading) return;
        const pasted = e.clipboardData.getData("text");
        if (!/[,\n]/.test(pasted)) return;
        e.preventDefault();
        setEmailChips((prev) => commitChipsFromText(pasted, prev));
        setEmailInput("");
        resetVerificationState();
    };

    const handleChipInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (loading) return;
        if (e.key === "Enter") {
            e.preventDefault();
            if (emailInput.trim()) {
                setEmailChips((prev) => commitChipsFromText(emailInput, prev));
                setEmailInput("");
                resetVerificationState();
            }
        } else if (e.key === "Backspace" && emailInput === "" && emailChips.length > 0) {
            setEmailChips((prev) => prev.slice(0, -1));
            resetVerificationState();
        }
    };

    const removeChip = (index: number) => {
        if (loading) return;
        setEmailChips((prev) => prev.filter((_, i) => i !== index));
        resetVerificationState();
    };

    // "email_verifier_YYYYMMDDHHMMSS" - a stable, sortable, machine-generated
    // name shared by every RequestHistory row one Quick Verify submission
    // creates (the backend creates one independent row per submitted email -
    // see create_verifier_request - so it can pick each one up on the fast
    // single-email pipeline regardless of batch size). The backend groups
    // rows sharing this exact project_name back into one history/progress
    // row (see QUICK_VERIFY_PROJECT_PREFIX in verifier_requests.py).
    const buildQuickVerifyProjectName = () => {
        const now = new Date();
        const pad = (n: number) => String(n).padStart(2, "0");
        const stamp = `${now.getFullYear()}${pad(now.getMonth() + 1)}${pad(now.getDate())}${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
        return `email_verifier_${stamp}`;
    };

    // Submits every email (whether 1 or many) in one API call. The backend
    // creates one independent RequestHistory/RequestDetails row per email,
    // so each is tracked/polled by its own id below (see pollBatchResults).
    const submitQuickVerifyBatch = async (emails: string[]): Promise<{ requestIds: number[]; acceptedEmails: string[] }> => {
        try {
            const response = await apiClient.post("/verifier-requests/", {
                project_name: buildQuickVerifyProjectName(),
                request_body: emails.map((email) => ({ email })),
            });
            const histories = Array.isArray(response?.data?.histories) && response.data.histories.length > 0
                ? response.data.histories
                : (response?.data?.history ? [response.data.history] : []);
            const requestIds: number[] = histories.map((h: any) => h.id).filter(Boolean);
            // The backend may accept fewer emails than submitted (e.g.
            // truncated to fit the user's remaining credits) - it always
            // keeps the first N in submission order, so slicing here mirrors
            // exactly what it created a row for.
            const acceptedEmails = emails.slice(0, requestIds.length);
            return { requestIds, acceptedEmails };
        } catch (error: any) {
            if (await handlePhoneError(error, () => handleVerify())) {
                // The modal is open; the same emails are submitted once verified.
            } else if (error?.response?.status === 401) {
                setHistoryError("Authentication failed. Please log in again.");
            } else {
                setQuickVerifyError(
                    error?.response?.data?.message ||
                    "Unable to submit email(s) for verification. Please try again."
                );
            }
            return { requestIds: [], acceptedEmails: [] };
        }
    };

    const pollBatchResults = async (requestIds: number[], total: number, runSeq: number) => {
        // Tighter interval (was 2000ms/15 attempts = 30s total) so small/medium
        // batches that finish in a couple of seconds have a real chance of
        // being sampled mid-flight instead of always landing on "already
        // fully done" at the very first check - same ~30s total budget.
        const maxAttempts = 40;
        const pollInterval = 750;

        for (let attempt = 0; attempt < maxAttempts; attempt++) {
            if (runSeq !== verifyRunSeqRef.current) return;
            await new Promise((resolve) => setTimeout(resolve, pollInterval));
            if (runSeq !== verifyRunSeqRef.current) return;
            try {
                const response = await apiClient.get("/verifier-requests/re/single", {
                    params: { ids: requestIds.join(",") },
                });
                const items = response?.data?.items || [];

                if (runSeq !== verifyRunSeqRef.current) return;

                // Capture the merged results synchronously so progress can be
                // derived from actual verification outcomes, not just from
                // how many submit calls have resolved.
                let updatedResults: any[] = [];
                setVerificationResults((prev) => {
                    updatedResults = prev.map((result) => {
                        const match = items.find((item: any) => item.id === result.requestId);
                        return match?.status && (match.status !== result.status || match.status_label !== result.status_label)
                            ? { ...result, status: match.status, status_label: match.status_label }
                            : result;
                    });
                    return updatedResults;
                });

                const verifiedSoFar = Math.max(
                    updatedResults.filter((r) => !isVerifying(r.status)).length,
                    peakProcessedCountRef.current
                );
                peakProcessedCountRef.current = verifiedSoFar;
                setProcessedCount(verifiedSoFar);
                setProgress(total > 0 ? Math.round((verifiedSoFar / total) * 100) : 0);

                if (items.length > 0 && items.every((item: any) => !isVerifying(item.status))) {
                    break;
                }
            } catch (error: any) {
                break;
            }
        }

        if (runSeq !== verifyRunSeqRef.current) return;

        // A small batch (or a batch that's already fully verified by the
        // time of the very first poll) can reach its terminal state on
        // attempt 1 - without this pause, `loading` flips false in the same
        // tick as the final setProgress/setProcessedCount update, so React
        // batches them together and the completed 100% state (or the
        // per-chip colors) never actually gets painted before the whole
        // progress card disappears. This just lets that final frame render.
        await new Promise((resolve) => setTimeout(resolve, 700));
        if (runSeq !== verifyRunSeqRef.current) return;

        setLoading(false);
        setEmailChips([]);
        setEmailInput("");
        setCurrentPage(1);
        await fetchHistory(1, "single", true);
    };

    const handleVerify = async () => {
        // Synchronous re-entrancy guard: `loading` is React state, so it
        // doesn't block a second invocation until after a re-render - a fast
        // double-click (or Enter + click landing together) can fire this
        // function twice before the button actually becomes disabled. This
        // ref is checked/set immediately, with no render in between, so a
        // second call bails out here instead of starting a second, competing
        // verification run.
        if (isSubmittingVerifyRef.current) return;
        const parsedEmails = getCombinedEmails();
        if (parsedEmails.length === 0 || loading) return;
        if (requirePhone(() => handleVerify())) return;

        isSubmittingVerifyRef.current = true;
        const runSeq = ++verifyRunSeqRef.current;
        peakProcessedCountRef.current = 0;

        try {
            setEmailChips(parsedEmails);
            setEmailInput("");

            setLoading(true);
            setHistoryError(null);
            setQuickVerifyError(null);
            setVerificationResults([]);
            setProgress(0);
            setProcessedCount(0);
            setShowContainer(true);

            // Optimistic total, shown immediately while the submit call is
            // in flight - corrected below once we know how many the backend
            // actually accepted (it may be fewer, e.g. truncated to fit a
            // credit limit).
            setBatchTotal(parsedEmails.length);

            // Every email - 1 or many - is submitted in one API call. The
            // backend creates one independent RequestHistory/RequestDetails
            // row per email (see create_verifier_request), so each is picked
            // up by the fast single-email verification pipeline regardless
            // of batch size, and tracked here by its own id (pollBatchResults
            // already handles any number of ids generically).
            const { requestIds, acceptedEmails } = await submitQuickVerifyBatch(parsedEmails);

            if (runSeq !== verifyRunSeqRef.current) return;

            if (requestIds.length === 0) {
                setVerificationResults(parsedEmails.map((email) => ({ email, status: "failed" })));
                setLoading(false);
                return;
            }

            setBatchTotal(acceptedEmails.length);

            // Track only the emails the backend actually accepted - the
            // dropped ones (if any) never get a request id, so they'd never
            // get a result back and stay stuck "processing" forever otherwise.
            setVerificationResults(
                acceptedEmails.map((email, idx) => ({ email, requestId: requestIds[idx], status: "processing" }))
            );

            setCurrentPage(1);

            // Deliberately NOT clearing emailChips/emailInput here - the chips
            // stay on screen (input stays disabled via `loading`) so the user
            // can watch each one's color update as pollBatchResults below
            // resolves real statuses. They're cleared once pollBatchResults
            // confirms verification has actually finished, ready for the next
            // batch.

            await fetchHistory(1, "single", true);

            if (runSeq !== verifyRunSeqRef.current) return;

            await pollBatchResults(requestIds, acceptedEmails.length, runSeq);
        } finally {
            isSubmittingVerifyRef.current = false;
        }
    };

    const handleBulkVerify = async () => {
        if (bulkFileProcessing) return;
        if (!bulkFile || bulkLoading) return;
        if (bulkError) return;
        if (bulkUploadError) return;

        setBulkLoading(true);
        setBulkUploadError(null);
        setHistoryError(null);
        // Shown straight away, while the file is still uploading - it's
        // closed again below if the upload fails or yields no valid emails.
        setShowBulkVerificationPopup(true);

        try {
            const formData = new FormData();
            formData.append("file", bulkFile);
            formData.append("project_name", fileName.replace(/\.[^/.]+$/, "") || "bulk_verification");

            const response = await apiClient.post("/verifier-requests/bulk", formData, {
                headers: {
                    "Content-Type": "multipart/form-data",
                },
            });

            // A 200/202 response with history.status === "failed" means the upload
            // itself succeeded but validation found no valid emails (e.g. wrong
            // column name). That's not a request error - it already shows up as a
            // "Failed" row in the history table below, so no separate banner needed.
            const hasValidEmails = response?.data?.history?.status !== "failed";

            setFileName("");
            setBulkFile(null);
            setBulkError(null);
            setBulkUploadError(null);

            if (!hasValidEmails) {
                setShowBulkVerificationPopup(false);
            }

            setCurrentPage(1);
            await fetchHistory(1, "bulk", true);
        } catch (error: any) {
            const data = error?.response?.data;
            // extractMessage falls through to this fallback only when the
            // backend sent no usable message - never surface error.message
            // here, since for a network failure that's axios's raw internal
            // string "Network Error", not something fit for a user to see.
            const message = extractMessage(data, "Unable to reach the server. Please check your connection and try again.");
            setShowBulkVerificationPopup(false);
            setBulkUploadError(message);
        } finally {
            setBulkLoading(false);
        }
    };

    const handleViewProgress = () => {
        setShowBulkVerificationPopup(false);
        historySectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    const downloadEmailVerificationResults = async (id: number) => {
        const stateKey = `results-${id}`;

        if (downloadingFiles[stateKey]) {
            return;
        }

        setDownloadingFiles(prev => ({ ...prev, [stateKey]: true }));
        try {
            const response = await apiClient.get(`/verifier-requests/re/${id}/download-results`, {
                responseType: "blob",
            });

            // This download button is shared by both tabs. Bulk rows always
            // have a real uploaded file_name - keep naming those downloads
            // after it, unchanged from before. Quick Verify rows (single
            // email or a combined batch) have no file_name, so use the
            // server-provided filename instead of falling back to the row's
            // email/label - an email address like "foo@bar.com" used as a
            // filename previously got its ".com" stripped by the old
            // extension-removal regex, leaving no extension at all and
            // making the browser's save dialog show it as an ambiguous file type.
            const item = historyItems.find((history) => history.id === id);
            // Always a clean ".csv" name - see utils/fileNames.
            const filename = downloadFileName(
                item?.file_name || response.headers["content-disposition"]?.split("filename=")[1]?.replace(/"/g, ""),
                `verification_results_${id}`
            );

            saveAs(response.data, filename);
        } catch (error: any) {
            if (await handlePhoneError(error, () => downloadEmailVerificationResults(id))) return;
            setHistoryError(
                error?.response?.data?.message ||
                "Failed to download verification results"
            );
        } finally {
            setDownloadingFiles(prev => ({ ...prev, [stateKey]: false }));
        }
    };

    const deleteVerificationHistory = async (id: number) => {
        const stateKey = `delete-${id}`;

        if (deletingFiles[stateKey]) {
            return;
        }

        setDeletingFiles(prev => ({ ...prev, [stateKey]: true }));
        try {
            await apiClient.delete(`/verifier-requests/${id}`);
            setDeleteConfirmId(null);
            await fetchHistory(currentPage, activeTab, true);
        } catch (error: any) {
            setHistoryError(
                error?.response?.data?.message ||
                "Failed to delete verification history"
            );
        } finally {
            setDeletingFiles(prev => ({ ...prev, [stateKey]: false }));
        }
    };

    const canDownloadResults = true;

    const handleTabClick = (tab: "single" | "bulk") => {
        if (tab === activeTab) return;
        setActiveTab(tab);
        // Only wipe the Quick Verify input/progress state when nothing is
        // running - a still-in-flight batch keeps updating this state in the
        // background (see pollBatchResults) regardless of which tab is
        // visible, so clearing it here would strand that poll loop writing
        // into an array it can never recover, permanently stuck at 0%. When
        // the user switches back to "single" mid-run, they should see the
        // batch exactly as it stands, not a reset one.
        if (!loading) {
            setEmailInput("");
            setEmailChips([]);
            setVerificationResults([]);
            setProgress(0);
            setProcessedCount(0);
        }
        setTotalPages(1);
        setCurrentPage(1);
        setHistoryError(null);
        setQuickVerifyError(null);
        setBulkUploadError(null);
        setGoToPageInput("");
        setHistorySearch("");
    };

    const clearFileSelection = () => {
        setFileName("");
        setBulkFile(null);
        setBulkError(null);
        setBulkUploadError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };

    const historyItemsForTab = historyItems.filter((item) => item.source === activeTab);

    // historyItems is already server-filtered by historySearch (see fetchHistory),
    // so it covers every page, not just whatever happened to be loaded locally.
    const displayedItems = historyItemsForTab;

    // A row is rendered with the bulk-style progress bar / valid-invalid-
    // catch-all counts whenever it covers more than one email - whether
    // that's a real bulk file upload, or a multi-email Quick Verify batch
    // (see submitQuickVerifyBatch). A lone single-email row (data_count
    // <= 1) keeps the plain status badge instead.
    const isBatchRow = (item: SingleVerifierHistoryItem) => (item.total_count || 0) > 1;

    const getBulkProgress = (item: SingleVerifierHistoryItem) => {
        if (item.status === "completed") return 100;
        if (item.status === "failed") return 0;
        if (typeof item.progress_percent === "number") {
            const normalized = Math.max(0, Math.min(100, item.progress_percent));
            return Math.trunc(normalized * 100) / 100;
        }
        if (item.processed_count !== undefined && item.total_count) {
            const pct = (item.processed_count / item.total_count) * 100;
            const normalized = Math.max(0, Math.min(100, pct));
            return Math.trunc(normalized * 100) / 100;
        }
        return 0;
    };

    const getBulkCounts = (item: SingleVerifierHistoryItem) => {
        const valid = Number(item.valid_count || 0);
        const invalid = Number(item.invalid_count || 0);
        const catchAll = Number(item.catch_all_count || 0);
        const risky = Number(item.risky_all_count || 0);
        const unknown = item.unknown_count === undefined || item.unknown_count === null ? null : Number(item.unknown_count);
        return { valid, invalid, catchAll, risky, unknown };
    };

    const optionalCount = (value?: number | null) =>
        value === undefined || value === null ? null : Number(value);

    const renderOutcomeIcons = (item: SingleVerifierHistoryItem) => {
        const counts = getBulkCounts(item);
        const isResolving = !!resolvingCatchAllIds[item.id];
        // Icons only - each count lives in its hover tooltip. Buckets the
        // backend doesn't report for a row show the label alone.
        const outcomes: { label: string; icon: string; count: number | null; variant: TooltipVariant }[] = [
            { label: "Valid", icon: "validate.svg", count: counts.valid, variant: "valid" },
            { label: "Invalid", icon: "invalid.svg", count: counts.invalid, variant: "invalid" },
            { label: "Catch-All", icon: "catch-all.svg", count: counts.catchAll, variant: "catchall" },
            { label: "Do Not Send", icon: "do-not-sent.svg", count: optionalCount(item.do_not_send_count) ?? counts.unknown, variant: "unknown" },
            { label: "Invalid Domain", icon: "invalid-domain.svg", count: optionalCount(item.invalid_domain_count), variant: "domain" },
            { label: "Risky", icon: "risk.svg", count: counts.risky, variant: "risky" },
        ];
        return (
            <div className="bulk-outcome">
                <div className="d-flex flex-row email-verify-details bulk-outcome-icons">
                    {outcomes.map((outcome) => {
                        const text = outcome.count === null ? outcome.label : `${outcome.label}: ${formatNumber(outcome.count)}`;
                        return (
                            <div
                                key={outcome.label}
                                className="icon-tooltip"
                                aria-label={text}
                                tabIndex={0}
                                onMouseEnter={(e) => showCountTooltip(e, text, outcome.variant)}
                                onMouseLeave={hideCountTooltip}
                                onFocus={(e) => showCountTooltip(e, text, outcome.variant)}
                                onBlur={hideCountTooltip}
                            >
                                <img className="bulk-outcome-icon" src={`/images/${outcome.icon}`} alt="" />
                            </div>
                        );
                    })}
                </div>

                {(item.source === "bulk" || isBatchRow(item)) && counts.catchAll > 0 && canResolveAgain(item) && (
                    <button
                        type="button"
                        className="resolve-catch-all-link"
                        onClick={() => openResolveCatchAll(item)}
                        disabled={isResolving}
                    >
                        {isResolving ? "Queuing..." : <>Resolve Catch-All <span aria-hidden="true">&rarr;</span></>}
                    </button>
                )}
            </div>
        );
    };

    // Catch-All Resolver progress for a row - same pill for bulk files,
    // Instant batches and single emails.
    const renderResolveProgress = (item: SingleVerifierHistoryItem) => {
        const resolve = item.catch_all_resolve;
        const percent = resolve?.status === "in_progress" && resolve.submitted > 0
            ? Math.min(99, Math.floor((resolve.resolved / resolve.submitted) * 100))
            : 0;
        return (
            <div className="bulk-resolving">
                <div className="bulk-status-pill bulk-status-resolving" aria-label={`In-Progress ${percent}%`}>
                    <div className="bulk-status-pill-row">
                        <span>In-Progress</span>
                        <span>{percent}%</span>
                    </div>
                    <div className="bulk-status-pill-track">
                        <div className="bulk-status-pill-fill" style={{ width: `${percent}%` }} />
                    </div>
                </div>
                <span className="bulk-resolving-label">
                    <svg className="spin" width="11" height="11" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                        <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z" />
                        <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z" />
                    </svg>
                    Resolving
                    {resolve?.status === "in_progress" && resolve.submitted > 0 && (
                        <span className="bulk-resolving-count">
                            {formatNumber(resolve.resolved)}/{formatNumber(resolve.submitted)}
                        </span>
                    )}
                </span>
            </div>
        );
    };

    // Result cell for a Bulk file upload row: Pending -> In-Progress -> Completed
    // (briefly) -> outcome icons, or Failed with a Retry action.
    const renderBulkStatus = (item: SingleVerifierHistoryItem) => {
        const status = (item.status || "").toLowerCase();

        if (status === "completed" && isResolvingCatchAll(item)) return renderResolveProgress(item);

        if (status === "completed" && !justCompletedIds.has(item.id)) return renderOutcomeIcons(item);

        if (status === "failed") {
            return (
                <div className="bulk-status-pill bulk-status-failed">
                    <div className="bulk-status-pill-row">
                        <span>Failed</span>
                        <button
                            type="button"
                            className="bulk-status-retry"
                            onClick={retryBulkVerification}
                            title="Upload the file again"
                        >
                            <svg width="12" height="12" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                                <path fillRule="evenodd" d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z" />
                                <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z" />
                            </svg>
                            Retry
                        </button>
                    </div>
                    <div className="bulk-status-pill-track" />
                </div>
            );
        }

        let variant = "progress";
        let label = "In-Progress";
        let percent = Math.min(99, Math.floor(getBulkProgress(item)));
        if (status === "completed") {
            variant = "completed";
            label = "Completed";
            percent = 100;
        } else if (status === "pending") {
            variant = "pending";
            label = "Pending";
        } else if (status === "paused") {
            variant = "pending";
            label = "Paused";
        }

        return (
            <div className={`bulk-status-pill bulk-status-${variant}`} aria-label={`${label} ${percent}%`}>
                <div className="bulk-status-pill-row">
                    <span>{label}</span>
                    <span>{percent}%</span>
                </div>
                <div className="bulk-status-pill-track">
                    <div className="bulk-status-pill-fill" style={{ width: `${percent}%` }} />
                </div>
            </div>
        );
    };

    const formatDate = (dateStr: string) => {
        return new Date(dateStr).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
        });
    };

    const formatDateWithTime = (dateStr: string) => {
        return new Date(dateStr).toLocaleString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const formatNumber = (value?: number | null) => {
        if (value === undefined || value === null || Number.isNaN(value)) return "0";
        return value.toLocaleString("en-US");
    };

    // Canonical per-email outcomes the backend guarantees once a request
    // reaches "completed" (see canonicalize_verifier_status in
    // backend/app/utils/enums.py, applied server-side in
    // get_re_single_verifier_history). "pending"/"processing"/"retrying" mean
    // verification hasn't finished yet. "failed" is the raw request-level
    // failure passthrough (e.g. no credits, unparseable file) - not a
    // per-email outcome, but still a terminal state for display purposes.
    const TERMINAL_RESULT_STATUSES = new Set([
        "verified",
        "invalid",
        "catch_all",
        "risky",
        "unknown",
        "failed",
    ]);

    const isVerifying = (value?: string) =>
        !TERMINAL_RESULT_STATUSES.has((value || "").trim().toLowerCase());

    // Buckets a result into the four pill/chip colors. Still-verifying values
    // fall into "catch" (neutral styling) since they aren't a real outcome yet.
    const classifyStatus = (value?: string): "valid" | "invalid" | "risky" | "catch" => {
        if (isVerifying(value)) return "catch";
        const status = (value || "").trim().toLowerCase();
        if (status === "verified") return "valid";
        if (status === "invalid" || status === "failed") return "invalid";
        if (status === "risky") return "risky";
        return "catch"; // catch_all, unknown
    };

    // const getResultStatusClass = (value?: string) => `pill pill-${classifyStatus(value)}`;

    const formatStatusLabel = (value?: string, label?: string | null) => {
        if (isVerifying(value)) return "Verifying";
        const lower = (value || "").trim().toLowerCase();
        if (lower === "verified") return "Valid";
        if (lower === "catch_all") return "Catch-All";
        if (lower === "risky") return "Risky";
        if (label && label.trim()) return label.trim();
        return (value as string).charAt(0).toUpperCase() + (value as string).slice(1);
    };

    return (
        <>
            {phoneModal}
            {showBulkVerificationPopup && (
                <div className="popup-overlay" onClick={() => setShowBulkVerificationPopup(false)}>
                    <div
                        className="popup-box"
                        style={{
                            backgroundImage: "url('/images/popup-back.png')",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "center",
                            backgroundSize: "inherit",
                        }}
                        onClick={(e) => e.stopPropagation()}
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby="bulk-verification-popup-title"
                    >
                        <div className="text-center bulk-verification-popup-body">
                            <div className="bulk-verification-icon-wrap">
                                <svg
                                    className="bulk-verification-icon"
                                    width="30"
                                    height="30"
                                    viewBox="0 0 24 24"
                                    xmlns="http://www.w3.org/2000/svg"
                                    aria-hidden="true"
                                    style={{ transform: "scaleX(-1)" }}
                                >
                                    <path
                                        fill="#00BF93"
                                        d="M13 3a9 9 0 0 0-9 9H1l3.89 3.89.07.14L9 12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 0-3.68-.79-4.94-2.06l-1.42 1.42A8.954 8.954 0 0 0 13 21a9 9 0 0 0 0-18zm-1 5v5l4.28 2.54.72-1.21-3.5-2.08V8H12z"
                                    />
                                </svg>
                                <div className="bulk-verification-icon-shadow" />
                            </div>

                            <p className="email-invitation" id="bulk-verification-popup-title">Email Verification in Progress</p>

                            <p className="invitation">
                                Your verification process has been initiated.
                                <br />
                                Please wait while we complete the checks.
                            </p>

                            <div className="d-flex flex-row justify-content-center" style={{ marginTop: 30 }}>
                                <button
                                    type="button"
                                    className="f-confirm"
                                    onClick={handleViewProgress}
                                    autoFocus
                                >
                                    View Progress
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {deleteConfirmId !== null && (
                <div className="popup-overlay" onClick={() => setDeleteConfirmId(null)}>
                    <div
                        className="popup-box"
                        style={{
                            backgroundImage: "url('/images/popup-back.png')",
                            backgroundRepeat: "no-repeat",
                            backgroundPosition: "center",
                            backgroundSize: "inherit",
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="d-flex flex-row justify-content-end">
                            <img
                                src="/images/cancel.png"
                                alt="close"
                                style={{ cursor: "pointer" }}
                                onClick={() => setDeleteConfirmId(null)}
                            />
                        </div>

                        <div className="text-center">
                            <p className="email-invitation">Delete this record?</p>

                            <p className="invitation">
                                This will permanently remove this verification history entry.
                                <br />
                                This action cannot be undone.
                            </p>

                            <div className="d-flex flex-row justify-content-center" style={{ marginTop: 30, gap: 12 }}>
                                <button
                                    type="button"
                                    className="delete-confirm-cancel"
                                    onClick={() => setDeleteConfirmId(null)}
                                    disabled={deletingFiles[`delete-${deleteConfirmId}`]}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    className="delete-confirm-danger"
                                    onClick={() => deleteVerificationHistory(deleteConfirmId)}
                                    disabled={deletingFiles[`delete-${deleteConfirmId}`]}
                                >
                                    {deletingFiles[`delete-${deleteConfirmId}`] ? "Deleting..." : "Delete"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="email-verify-heading">
                <div className="dashboard-page">
                    <h1>Verify Emails with Confidence</h1>
                    {/* <h1>Verify Smarter. Reach Faster.</h1> */}
                    <p>Verify individual emails or bulk lists to improve deliverability, reduce bounce rates, and protect your sender reputation.</p>
                </div>
                <CatchAllResolverPromo />
            </div>

            <div className="row">
                <div className="col-md-5">
                    {/* Tabs */}
                    <div className="email-tabs-wrapper">
                        <div
                            className={`email-tab ${activeTab === "single" ? "active" : ""}`}
                            onClick={() => handleTabClick("single")}
                        >
                            Instant Email Verification
                        </div>

                        <div
                            className={`email-tab ${activeTab === "bulk" ? "active" : ""}`}
                            onClick={() => handleTabClick("bulk")}
                        >
                            Bulk Email Verification
                        </div>
                    </div>

                    {/* Tab Containers */}
                    <div className="email-tab-content-wrapper">
                        {activeTab === "single" && (() => {
                            const parsedEmails = getCombinedEmails();
                            // The API folds Do Not Mail / Invalid Domain into "invalid";
                            // status_label keeps the real outcome.
                            const labelOf = (r: any) => (r.status_label || "").trim().toLowerCase();
                            const doNotMailCount = verificationResults.filter((r) => labelOf(r) === "do not mail").length;
                            const invalidDomainCount = verificationResults.filter((r) => labelOf(r) === "invalid domain").length;
                            const validCount = verificationResults.filter((r) => classifyStatus(r.status) === "valid").length;
                            const invalidCount =
                                verificationResults.filter((r) => classifyStatus(r.status) === "invalid").length - doNotMailCount - invalidDomainCount;
                            const riskyCount = verificationResults.filter((r) => classifyStatus(r.status) === "risky").length;
                            const catchCount =
                                verificationResults.length - validCount - invalidCount - riskyCount - doNotMailCount - invalidDomainCount;
                            // The batch's history row is its first request (the backend
                            // groups an Instant batch under its lowest id).
                            const batchRequestIds = verificationResults.map((r) => r.requestId).filter((id): id is number => typeof id === "number");
                            const batchRequestId = batchRequestIds.length ? Math.min(...batchRequestIds) : null;
                            // console.log("verificationResults",verificationResults);
                            // Batch summary card is only useful when there's an actual batch -
                            // a single verified email doesn't need a "1 email checked" summary.
                            const allVerified = verificationResults.length > 1 && verificationResults.every((r) => !isVerifying(r.status));
                            // console.log("allVerified",allVerified);
                            const getChipStatusClass = (email: string) => {
                                const result = verificationResults.find((r) => r.email === email);
                                return result ? `email-chip-${classifyStatus(result.status)}` : "";
                            };

                            return (
                                <div className="email-tab-content single-tab">
                                    <p className="individual">Enter one or more email addresses to verify mailbox validity before sending.</p>
                                    <div className="valid-email-box">
                                        <div className={`wrapper-back${emailChips.length > 0 || emailInput.trim().length > 0 ? " has-emails" : ""}`}>
                                            <div
                                                className="email-input-wrapper"
                                                onClick={() => chipInputRef.current?.focus()}
                                            >
                                                <div className="email-chip-box">
                                                    {emailChips.map((email, idx) => (
                                                        <span className={`email-chip ${getChipStatusClass(email)}`} key={`${email}-${idx}`}>
                                                            {email}
                                                            <button
                                                                type="button"
                                                                className="email-chip-remove"
                                                                aria-label={`Remove ${email}`}
                                                                disabled={loading}
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    removeChip(idx);
                                                                }}
                                                            >
                                                                &times;
                                                            </button>
                                                        </span>
                                                    ))}
                                                    <input
                                                        ref={chipInputRef}
                                                        type="text"
                                                        className="email-chip-input"
                                                        placeholder={
                                                            emailChips.length === 0
                                                                ? "Enter or paste up to 20 emails (separated by commas or new lines)"
                                                                : ""
                                                        }
                                                        value={emailInput}
                                                        onChange={handleChipInputChange}
                                                        onKeyDown={handleChipInputKeyDown}
                                                        onPaste={handleChipInputPaste}
                                                        disabled={loading}
                                                        aria-label="Email addresses"
                                                    />
                                                </div>

                                                <div className="email-input-divider" />

                                                <div className="email-input-actions">
                                                    <button
                                                        className="verify-btn"
                                                        onClick={handleVerify}
                                                        disabled={parsedEmails.length === 0 || loading}
                                                    >
                                                        {loading ? (
                                                            <span className="spinner" />
                                                        ) : (
                                                            "Verify"
                                                        )}
                                                    </button>
                                                </div>
                                            </div>



                                        </div>

                                        {quickVerifyError && (
                                            <p className="bulk-upload-error-text mt-2">{quickVerifyError}</p>
                                        )}

                                        {loading && (
                                            <div className="wrapper-back-1">
                                                <div className="d-flex flex-row justify-content-between">
                                                    <div>
                                                        <p className="verification-progress">Verification progress</p>
                                                    </div>
                                                    <div>
                                                        <p className="cent">{progress}%</p>
                                                    </div>
                                                </div>
                                                <div className="progress-wrapper" aria-label="Verification progress">
                                                    <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
                                                    <span className="progress-text">{progress}%</span>
                                                </div>

                                                <p className="email-progress">{processedCount} of {batchTotal} email{batchTotal === 1 ? "" : "s"} processed</p>
                                            </div>
                                        )}

                                        {allVerified && (
                                            <div className="batch-summary-card">
                                                <div className="batch-summary-header">
                                                    <img src="/images/batch-summary.png" alt="" />
                                                    <span>Batch Summary Card</span>
                                                    {/* Lists of 2+ emails only - not offered for a single email. */}
                                                    {catchCount > 0 && batchRequestId !== null && verificationResults.length > 1 && (
                                                        <button
                                                            type="button"
                                                            className="batch-summary-resolve"
                                                            onClick={() => setResolveCatchAllTarget({ id: batchRequestId, count: catchCount })}
                                                            disabled={!!resolvingCatchAllIds[batchRequestId] || resolveQueuedIds.has(batchRequestId)}
                                                        >
                                                            {resolveQueuedIds.has(batchRequestId) ? "Resolving…" : "Catch-All Resolver"}
                                                        </button>
                                                    )}
                                                </div>
                                                <p className="batch-summary-line">
                                                    Verification Complete: {verificationResults.length} Email{verificationResults.length === 1 ? "" : "s"} Checked.
                                                </p>
                                                <div className="batch-summary-pills">
                                                    <span className="pill pill-valid"><img src="/images/validate.png" alt="" />&nbsp;{validCount} Valid</span>
                                                    <span className="pill pill-catch">
                                                        <svg width="11" height="11" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                                                            <path d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14zm0 1A8 8 0 1 1 8 0a8 8 0 0 1 0 16z" />
                                                            <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                                                        </svg>
                                                        &nbsp;{catchCount} Catch-All
                                                    </span>
                                                    <span className="pill pill-risky">
                                                        <svg className="risky-icon" width="11" height="11" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                                                            <path d="M7.938 2.016A.13.13 0 0 1 8.002 2a.13.13 0 0 1 .063.016.146.146 0 0 1 .054.057l6.857 11.667c.036.06.035.124.002.183a.163.163 0 0 1-.054.06.116.116 0 0 1-.066.017H1.146a.115.115 0 0 1-.066-.017.163.163 0 0 1-.054-.06.176.176 0 0 1 .002-.183L7.884 2.073a.147.147 0 0 1 .054-.057m1.044-.45a1.13 1.13 0 0 0-1.96 0L.165 13.233c-.457.778.091 1.767.98 1.767h13.713c.889 0 1.438-.99.98-1.767L8.982 1.566z" />
                                                            <path d="M7.002 12a1 1 0 1 1 2 0 1 1 0 0 1-2 0M7.1 5.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0z" />
                                                        </svg>
                                                        &nbsp;{riskyCount} Risky
                                                    </span>
                                                    <span className="pill pill-invalid"><img src="/images/invalidate.png" alt="" />&nbsp;{invalidCount} Invalid</span>
                                                    <span className="pill pill-dnm"><img src="/images/do-not-sent.svg" alt="" width={11} height={11} />&nbsp;{doNotMailCount} Do Not Mail</span>
                                                    <span className="pill pill-domain"><img src="/images/invalid-domain.svg" alt="" width={11} height={11} />&nbsp;{invalidDomainCount} Invalid Domain</span>
                                                </div>
                                            </div>
                                        )}

                                    </div>

                                </div>
                            );
                        })()}

                        {activeTab === "bulk" && (
                            <div className="email-tab-content bulk-tab">
                                <div className="d-flex flex-row justify-content-between">
                                    <div>
                                        <p className="uploaded-lists">Upload Email List</p>
                                    </div>
                                </div>

                                <div className="back-config">
                                    <div
                                        ref={uploadAreaRef}
                                        className={`upload-box text-center ${fileName ? "uploaded" : ""}`}
                                        onDragOver={(e) => e.preventDefault()}
                                        onDrop={handleDrop}
                                        style={{ position: "relative", boxShadow: "0 4px 12px #0000000D" }}
                                    >
                                        <input
                                            type="file"
                                            id="fileUpload"
                                            accept={SUPPORTED_BULK_EXTENSIONS.join(",")}
                                            hidden
                                            ref={fileInputRef}
                                            onChange={handleFileChange}
                                        />

                                        <img
                                            src={fileName ? "/images/uplod-anable.png" : "/images/uplod-disable.png"}
                                            alt="upload"
                                            className="upload-image"
                                            role="button"
                                            tabIndex={0}
                                            onClick={() => fileInputRef.current?.click()}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter" || e.key === " ") {
                                                    e.preventDefault();
                                                    fileInputRef.current?.click();
                                                }
                                            }}
                                        />

                                        {!fileName && (
                                            <p
                                                className="drag"
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => fileInputRef.current?.click()}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        fileInputRef.current?.click();
                                                    }
                                                }}
                                            >
                                                Drag & drop your 
                                            </p>
                                        )}

                                        {!fileName && (
                                            <p
                                                className="drag-1"
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => document.getElementById("fileUpload")?.click()}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter" || e.key === " ") {
                                                        e.preventDefault();
                                                        document.getElementById("fileUpload")?.click();
                                                    }
                                                }}
                                            >
                                            <span className="spam">CSV</span> or <span className="spam">Excel file here</span>
                                            </p>
                                        )}

                                        {fileName && (
                                            <>
                                                <button
                                                    type="button"
                                                    aria-label="Remove selected file"
                                                    onClick={clearFileSelection}
                                                    style={{
                                                        position: "absolute",
                                                        top: "12px",
                                                        right: "12px",
                                                        width: "18px",
                                                        height: "18px",
                                                        border: "none",
                                                        background: "#FFFFFF",
                                                        fontSize: "18px",
                                                        lineHeight: "18px",
                                                        fontWeight: 700,
                                                        color: "#7E8C9C",
                                                        borderRadius: "50%",
                                                        padding: 0,
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    ×
                                                </button>
                                                <p className="file-name mt-2">{fileName}</p>
                                                <p className="ready-text">Ready to submit</p>
                                            </>
                                        )}
                                    </div>

                                    {bulkError && <p className="bulk-upload-error-text mt-2">{bulkError}</p>}

                                    {bulkUploadError && (
                                        <p className="bulk-upload-error-text mt-2">{bulkUploadError}</p>
                                    )}

                                    <button
                                        type="button"
                                        className={`verified ${!fileName || bulkLoading || bulkFileProcessing ? "disabled" : ""}`}
                                        disabled={!fileName || bulkLoading || bulkFileProcessing}
                                        onClick={handleBulkVerify}
                                    >
                                        {bulkLoading ? (
                                            <span className="spinner" />
                                        ) : (
                                            "Start Verification"
                                        )}
                                    </button>

                                </div>



                            </div>
                        )}
                    </div>
                </div>

                <div className="col-md-7">
                    <div className="verification-history" ref={historySectionRef}>
                        <div className="d-flex flex-row justify-content-between align-items-center verification-history-header">
                            <div className="d-flex flex-row">
                                <div>
                                    <img src="/images/verification.png" alt="" />
                                </div>
                                <div>
                                    <p className="verification-history-text">Verification History</p>
                                </div>
                            </div>

                            {(historyItemsForTab.length > 0 || historySearch.trim().length > 0) && (
                                <div className="search-wrapper">
                                    <img
                                        src="/images/search-icon.png"
                                        alt="search"
                                        className="search-icon left"
                                    />
                                    <input
                                        type="text"
                                        className="search-input"
                                        placeholder="Search file names or email address..."
                                        autoComplete="off"
                                        value={historySearch}
                                        onChange={(e) => {
                                            setHistorySearch(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </div>
                            )}
                        </div>
                        {historyError && (
                            <p className="bulk-upload-error-text">
                                {historyError}
                            </p>
                        )}

                        {historyLoading ? (
                            <div className="table-wrapper email-verify-table-wrapper">
                                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "24px" }}>
                                    <div className="spinner-border" role="status">
                                        <span className="visually-hidden">Loading...</span>
                                    </div>
                                </div>
                            </div>
                        ) : displayedItems.length === 0 ? (
                            <div className="verification-history-empty">
                                <p className="verification-history-empty-title">No Lists Yet</p>
                                <p className="verification-history-empty-text">Get started by creating your first email varication.</p>
                            </div>
                        ) : (
                            <div className="table-wrapper email-verify-table-wrapper">
                                <table className="custom-table">
                                    <thead>
                                        <tr>
                                            <th>Email / File Name</th>
                                            <th>Verified On</th>
                                            <th>Emails</th>
                                            <th>Results</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            displayedItems.map((item) => (
                                                <tr key={item.id}>
                                                    <td>
                                                        <span
                                                            title={item.label}
                                                            style={{
                                                                maxWidth: activeTab === "single" ? 160 : 180,
                                                                display: "inline-block",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                                verticalAlign: "bottom",
                                                                cursor: "pointer",
                                                            }}
                                                        >
                                                            {item.label}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <span
                                                            title={formatDateWithTime(item.created_at)}
                                                            style={{
                                                                maxWidth: 140,
                                                                display: "inline-block",
                                                                overflow: "hidden",
                                                                textOverflow: "ellipsis",
                                                                whiteSpace: "nowrap",
                                                                verticalAlign: "bottom",
                                                                cursor: "pointer",
                                                            }}
                                                        >
                                                            {formatDate(item.created_at)}
                                                        </span>
                                                    </td>
                                                    <td>{formatNumber(item.total_count ?? item.processed_count ?? 0)}</td>
                                                    <td>
                                                        {item.source === "bulk" ? (
                                                            renderBulkStatus(item)
                                                        ) : isBatchRow(item) ? (
                                                            (() => {
                                                                const bulkProgress = getBulkProgress(item);
                                                                const status = (item.status || "").toLowerCase();

                                                                if (status === "pending") return <span className="catch-all">Pending</span>;
                                                                if (status === "failed") return <span className="invalid-td">Failed</span>;
                                                                if (bulkProgress >= 100) return isResolvingCatchAll(item) ? renderBulkStatus(item) : renderOutcomeIcons(item);

                                                                // Quick Verify already has its own live progress bar +
                                                                // Batch Summary Card in the panel on the left while it's
                                                                // running, so a still-processing Quick Verify row in this
                                                                // table just shows a plain "Verifying" badge until it's done.
                                                                return <span className="catch-all">Verifying</span>;
                                                        })()
                                                    ) : (item.status || "").toLowerCase() === "failed" ? (
                                                        <span className="invalid-td">Failed</span>
                                                    ) : isResolvingCatchAll(item) ? (
                                                        renderResolveProgress(item)
                                                    ) : (
                                                        <div className="single-status-cell">
                                                        <span
                                                            className={(() => {
                                                                const status = (item.status || "").trim().toLowerCase();
                                                                if (isVerifying(status)) return "catch-all";
                                                                if (status === "verified") return "valid-td";
                                                                if (status === "invalid") return "invalid-td";
                                                                if (status === "risky") return "risky-td";
                                                                if (status === "catch_all") return "catchall-td";
                                                                return "catch-all"; // unknown
                                                            })()}
                                                        >
                                                            {formatStatusLabel(item.status, item.status_label)}
                                                        </span>
                                                        </div>
                                                    )}
                                                    </td>
                                                    <td>
                                                        <div className="d-flex flex-row gap-2">
                                                            {(item.source === "bulk" || isBatchRow(item)) && (
                                                                <>
                                                                    <button
                                                                        className="btn btn-link p-0"
                                                                        disabled={!canViewResults(item)}
                                                                        onClick={() => {
                                                                            const counts = getBulkCounts(item);
                                                                            navigate(`/email-verifier/results/${item.id}`, {
                                                                                state: {
                                                                                    fileName: item.file_name || item.label,
                                                                                    counts: {
                                                                                        valid: counts.valid,
                                                                                        invalid: counts.invalid,
                                                                                        catchall: counts.catchAll,
                                                                                        donotsend: item.do_not_send_count ?? counts.unknown ?? 0,
                                                                                        domain: item.invalid_domain_count ?? 0,
                                                                                        risky: counts.risky,
                                                                                    },
                                                                                },
                                                                            });
                                                                        }}
                                                                        style={{ cursor: canViewResults(item) ? "pointer" : "not-allowed", pointerEvents: "auto" }}
                                                                        aria-label="View results"
                                                                        title={canViewResults(item) ? "View results" : "Results are available once verification completes"}
                                                                    >
                                                                        <img className={`table-action-icon${canViewResults(item) ? "" : " disabled"}`} src="/images/eye-icon.svg" alt="View" />
                                                                    </button>

                                                                    <span className="action-divider" />
                                                                </>
                                                            )}

                                                            {item.source === "single" && item.email && !isBatchRow(item) && (() => {
                                                                // One email: open its Email Details page.
                                                                const ready = !isVerifying(item.status) && (item.status || "").toLowerCase() !== "failed";
                                                                return (
                                                                    <>
                                                                        <button
                                                                            className="btn btn-link p-0"
                                                                            disabled={!ready}
                                                                            onClick={() => navigate(`/email-verifier/email/${item.id}`)}
                                                                            style={{ cursor: ready ? "pointer" : "not-allowed", pointerEvents: "auto" }}
                                                                            aria-label="View email details"
                                                                            title={ready ? "View email details" : "Details are available once verification completes"}
                                                                        >
                                                                            <img className={`table-action-icon${ready ? "" : " disabled"}`} src="/images/eye-icon.svg" alt="View" />
                                                                        </button>

                                                                        <span className="action-divider" />
                                                                    </>
                                                                );
                                                            })()}

                                                            <button
                                                                className="btn btn-link p-0"
                                                                disabled={!item.can_download_results || !canDownloadResults}
                                                                onClick={() => downloadEmailVerificationResults(item.id)}
                                                                style={{
                                                                    cursor: item.can_download_results && canDownloadResults ? "pointer" : "not-allowed",
                                                                    color: item.can_download_results && canDownloadResults ? undefined : "#d9534f",
                                                                    pointerEvents: "auto",
                                                                }}
                                                                aria-label="Download results"
                                                            >
                                                                {downloadingFiles[`results-${item.id}`] ? (
                                                                    <div className="spinner-border spinner-border-sm" role="status">
                                                                        <span className="visually-hidden">Downloading...</span>
                                                                    </div>
                                                                ) : (
                                                                    <img className={`table-action-icon${item.can_download_results && canDownloadResults ? "" : " disabled"}`} src="/images/download-icon-v.svg" alt="Download" />
                                                                )}
                                                            </button>

                                                            <span className="action-divider" />

                                                            <button
                                                                className="btn btn-link p-0"
                                                                onClick={() => setDeleteConfirmId(item.id)}
                                                                aria-label="Delete verification history"
                                                            >
                                                                {deletingFiles[`delete-${item.id}`] ? (
                                                                    <div className="spinner-border spinner-border-sm" role="status">
                                                                        <span className="visually-hidden">Deleting...</span>
                                                                    </div>
                                                                ) : (
                                                                    <img className="table-action-icon" src="/images/delete-icon.svg" alt="Delete" />
                                                                )}
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        }
                                    </tbody>
                                </table>
                            </div>
                        )}

                        {totalPages > 1 && (
                            <div className="pagination-container">
                                <button
                                    className="pagination-arrow"
                                    onClick={() => handlePageChange(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    &lt;
                                </button>

                                {renderPageNumbers().map((page, index) => (
                                    page === "..." ? (
                                        <span key={`ellipsis-${index}`} className="pagination-ellipsis">...</span>
                                    ) : (
                                        <button
                                            key={page}
                                            className={`pagination-number ${currentPage === page ? "active" : ""}`}
                                            onClick={() => handlePageChange(page as number)}
                                        >
                                            {page}
                                        </button>
                                    )
                                ))}

                                <button
                                    className="pagination-arrow"
                                    onClick={() => handlePageChange(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    &gt;
                                </button>

                                <div className="go-to-page">
                                    <span>Go to page</span>
                                    <input
                                        type="number"
                                        min="1"
                                        max={totalPages}
                                        value={goToPageInput}
                                        onChange={(e) => setGoToPageInput(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === "Enter") {
                                                handleGoToPage();
                                            }
                                        }}
                                        onBlur={handleGoToPage}
                                        className="page-input"
                                    />
                                </div>
                            </div>
                        )}

                    </div>
                </div>
            </div>

            {resolveCatchAllTarget && (
                <ResolveCatchAllModal
                    requestId={resolveCatchAllTarget.id}
                    emailCount={resolveCatchAllTarget.count}
                    submitting={!!resolvingCatchAllIds[resolveCatchAllTarget.id]}
                    onClose={closeResolveCatchAll}
                    onConfirm={(selection) => resolveCatchAll(resolveCatchAllTarget.id, selection)}
                />
            )}

            {countTooltip && createPortal(
                <span
                    className={`tooltip-text tooltip-portal tooltip-outcome tooltip-${countTooltip.variant}`}
                    style={{ top: countTooltip.top, left: countTooltip.left }}
                >
                    {countTooltip.text}
                </span>,
                document.body
            )}

        </>
    );
};

export default Email_Verify;
